import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import every from 'lodash/every';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import mergeWith from 'lodash/mergeWith';
import noop from 'lodash/noop';
import omit from 'lodash/omit';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import { FormContainer, FormSection } from '@ad-scripts/presentation/components/common/forms/form_layout';
import FormContext from 'src/presentation/contexts/FormContext';
import generateId from 'src/lib/generateId';
import useInitialItem from 'src/presentation/hooks/useInitialItem';
import useRouter from 'src/presentation/hooks/useRouter';

const Body = styled(FormContainer)`
  flex-grow: 1;
  max-width: 500px;
  width: 80%;
`;

export const FormContentWrapper = styled.div`
  display: inherit; // inherit styles of parent container to avoid duplicates
  flex-direction: inherit;
  align-items: inherit;
  height: 100%;
  width: 100%;

  &:focus {
    outline: none; // adjusts the 'focus' state caused by tabIndex="0"
  }
`;

export { Body, FormSection as Section };

type FormProps = {
  context: any;
  'data-aid'?: string;
  preventSaveOnEnter?: boolean;
};

export type FormEditorProps = {
  actionsRef?: any;
  errors: any;
  item?: any;
  persistingState?: string;
  root: string;
  context?: any;
};

const Form: FC<FormProps> = ({ context, children, preventSaveOnEnter }) => {
  const { onSave } = context;

  const onKeyDown = useCallback(
    evt => {
      if (!preventSaveOnEnter && evt.key === 'Enter') {
        isFunction(onSave) && onSave();
      }
    },
    [onSave, preventSaveOnEnter]
  );

  return (
    <FormContext.Provider value={context}>
      <FormContentWrapper children={children} onKeyDown={onKeyDown} tabIndex="0" />
    </FormContext.Provider>
  );
};

type FunctionOrFunctionArray = Function | Function[];

export interface UseFormProps<T extends object = object> {
  add?: any;
  defaultItem?: Partial<T>;
  errors?: any;
  item?: T;
  manualScrollToFirstError?: boolean;
  onBlur?: Function;
  onDelete?: Function;
  onSave?: Function;
  resetErrors: FunctionOrFunctionArray;
  root?: any;
  saveAsNew?: boolean;
  update?: any;
  updateBatch?: any;
  validate?: any;
}

export function useForm<T extends object>(props: UseFormProps<T>) {
  const {
    add,
    defaultItem,
    errors,
    item,
    manualScrollToFirstError,
    onBlur,
    onDelete,
    onSave,
    resetErrors,
    root,
    saveAsNew,
    update,
    validate,
  } = props;
  const { history } = useRouter();
  const workingItem = useInitialItem(item, defaultItem);

  const dispatch = useDispatch();
  const [workingState, setWorkingState] = useState<T>(workingItem);

  const workingStateRef = useRef<T>(workingState);

  // updates specific properties in state and merges into old values
  const updateState = useCallback((newAttributes, parameters = {}) => {
    const attrs = omit(newAttributes, 'id');

    const newState = mergeWith({}, workingStateRef.current, attrs, (objValue, srcValue) => {
      if (Array.isArray(objValue)) {
        // only arrays of plain values have to be replaced with new ones;
        // arrays of objects have to be updated, not replaced, unless replaceArrayValues is set
        const arrayValuesShouldBeReplaced = parameters.replaceArrayValues || every(objValue, item => !isObject(item));

        if (arrayValuesShouldBeReplaced) {
          return srcValue;
        }
      }
    });
    setWorkingState(newState);
    workingStateRef.current = newState;
  }, []);

  // fully replaces the working state with new value
  const overrideState = useCallback(newState => {
    setWorkingState(newState);
    workingStateRef.current = newState;
  }, []);

  const resetAllErrors = useCallback(
    (attr?) => {
      getDispatcherForAll(dispatch, resetErrors)(attr);
    },
    [dispatch, resetErrors]
  );

  const validateState = useCallback(() => {
    resetAllErrors();
    // TODO: run more than one validation function https://app.shortcut.com/gladly/story/168821
    validate ? dispatch(validate(workingStateRef.current)) : noop();
  }, [dispatch, resetAllErrors, validate]);

  const resetError = useCallback(
    ({ attr, errors }) => {
      if (!errors || !errors.find(({ attr: eAttr }) => eAttr === attr)) {
        return;
      }
      resetAllErrors(attr);
    },
    [resetAllErrors]
  );

  const isNew = isNil(saveAsNew) ? !item : !!saveAsNew;
  const onClose = useCallback(() => {
    setWorkingState(workingItem);
    resetAllErrors();
    const historyConfig = history.location?.search ? { pathname: root, search: history.location.search } : root;
    history.push(historyConfig);
  }, [history, root, resetAllErrors, workingItem]);

  const formContext = useMemo(
    () => ({
      errors,
      isNew,
      item: workingItem,
      onClose,
      overrideState,
      resetError,
      updateState,
      validateState,
      workingState,
      workingStateRef,
    }),
    [
      errors,
      isNew,
      workingItem,
      onClose,
      overrideState,
      resetError,
      updateState,
      validateState,
      workingState,
      workingStateRef,
    ]
  );

  const defaultOnSave = useCallback(() => {
    const currentWorkingState = workingStateRef.current;
    resetAllErrors();
    if (validate && !dispatch(validate(currentWorkingState))) {
      return;
    }

    isNew
      ? dispatch(add({}, { id: generateId(), ...currentWorkingState }))
      : dispatch(update({ id: workingItem.id }, currentWorkingState));
  }, [add, dispatch, isNew, resetAllErrors, update, validate, workingItem.id]);

  const onSaveHandler = useMemo(
    () => (isFunction(onSave) ? () => onSave({ ...formContext, ...props, defaultOnSave }) : defaultOnSave),
    [onSave, formContext, props, defaultOnSave]
  );

  const value = useMemo(() => {
    return {
      ...formContext,
      onSave: onSaveHandler,
      ...(onBlur
        ? {
            onBlur: ({ currentState, needToOverrideState }) =>
              onBlur({ ...formContext, currentState, needToOverrideState, ...props }),
          }
        : {}),
      ...(onDelete ? { onDelete: (itemId, version) => onDelete({ ...formContext, version, itemId, ...props }) } : {}),
    };
  }, [formContext, onBlur, onDelete, onSaveHandler, props]);

  useEffect(() => {
    workingStateRef.current = workingState;
  }, [workingState]);

  useEffect(() => {
    if (!manualScrollToFirstError) {
      scrollToFirstError({ errors });
    }
  }, [manualScrollToFirstError, errors]);

  return value;
}

export function scrollToFirstError({ errors }) {
  if (isEmpty(errors)) {
    return;
  }
  const invalidElement = document.querySelector('.invalidError');
  invalidElement &&
    isFunction(invalidElement.scrollIntoView) &&
    invalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

export function useStandardRepoOnSave(options?: { updateSuccessToastMessage: string }) {
  const dispatch = useDispatch();
  return ({ add, isNew, item, resetErrors, update, validate, workingStateRef }) => {
    getDispatcherForAll(dispatch, resetErrors)();

    const currentWorkingState = workingStateRef.current;
    if (validate && !dispatch(validate(currentWorkingState))) {
      return;
    }

    isNew
      ? dispatch(add({}, { id: generateId(), ...currentWorkingState, version: 1 }))
      : dispatch(
          update(
            { id: item.id },
            { ...currentWorkingState, version: currentWorkingState.version + 1 },
            { successToastMessage: options?.updateSuccessToastMessage }
          )
        );
  };
}

function getDispatcherForAll(dispatch: Function, funcOrArray: FunctionOrFunctionArray) {
  return (arg?) => {
    if (typeof funcOrArray === 'function') {
      dispatch(funcOrArray(arg));
    } else {
      forEach(funcOrArray, f => dispatch(f(arg)));
    }
  };
}

export default Form;
